// Section 1256 module: one basis per futures account (the 1099-B, or by explicit decision a full-year statement), then 40/60.
import { checkBox11, split1256, type TraceStep } from "../calc.ts";
import { D, type Dec, ZERO, fmt } from "../money.ts";
import { IssueList, m, resultFrom, usd, type ModuleResult } from "./issues.ts";
import { formName, type InfoReturn, type SourceView } from "./sources.ts";
import type { Account, Decision, Id, Money, Source, YearFile } from "./types.ts";
import { boxValue, known, type Amount } from "./values.ts";
import { live } from "./yearfile.ts";

export interface FuturesAccountResult {
  accountId: Id;
  label: string;
  ownerId: Id | "joint";
  basis: Money;
  basisFrom: "form_1099b" | "statement" | "no_activity";
  sourceIds: Id[];
}

export interface FuturesResult {
  perAccount: FuturesAccountResult[];
  /** Net Section 1256 gain or loss by owner; "joint" for joint accounts. */
  perOwner: Record<string, Money>;
  total: Money;
  shortTerm: Money;
  longTerm: Money;
}

export interface FuturesReconciliation {
  accountId: Id;
  forms: { sourceId: Id; box8: Amount; box9: Amount; box10: Amount; box11: Amount; computedBox11: Money | null; difference: Money | null }[];
  /** Statement figure (realized − prior year-end open + year-end open) minus the 1099-B. Shown, never used. */
  statementDifference: Money | null;
}

const SCREEN_1256 = ["nonresident", "section475", "straddlesOrHedges", "nonUsd"] as const;
const SCREEN_LABEL: Record<string, string> = {
  nonresident: "not being a US resident",
  entity: "trading through a partnership or corporation",
  section475: "a Section 475 mark-to-market election",
  straddlesOrHedges: "straddles or hedging transactions",
  nonUsd: "accounts or payouts in another currency",
  ownAccountTraderDeductions: "claiming trader tax status deductions",
};

export function screeningIssues(yf: YearFile, personId: Id, keys: readonly string[], module: "section1256" | "propBusiness" | "selfEmployment", issues: IssueList) {
  const s = yf.screening[personId] as unknown as Record<string, "yes" | "no" | null> | undefined;
  const label = yf.people.find((p) => p.id === personId)?.label ?? "This person";
  const unanswered = keys.filter((k) => !s || s[k] === null || s[k] === undefined);
  const yes = keys.filter((k) => s?.[k] === "yes");
  if (unanswered.length)
    issues.add({
      code: "SCREENING_NEEDED",
      severity: "blocker",
      module,
      personId,
      refs: [personId],
      message: `We need a few yes/no answers about ${label}'s situation first.`,
      nextAction: `Answer the questions about ${unanswered.map((k) => SCREEN_LABEL[k]).join(", ")}.`,
    });
  for (const k of yes)
    issues.add({
      code: "UNSUPPORTED_SITUATION",
      severity: "blocker",
      module,
      personId,
      refs: [personId],
      message: `This report doesn't cover ${SCREEN_LABEL[k]}. Your records stay organized for your preparer.`,
      nextAction: "Share the organized records with a tax professional for this part.",
    });
}

function boxIssue(f: InfoReturn, box: string, a: Amount, issues: IssueList, module: "section1256" | "propBusiness", personId: Id | null) {
  const what = a.state === "blank" ? "is blank" : a.state === "unreadable" ? `couldn't be read ("${a.raw}")` : "hasn't been entered";
  issues.add({
    code: a.state === "blank" ? "FIELD_BLANK" : a.state === "unreadable" ? "FIELD_UNREADABLE" : "FIELD_MISSING",
    severity: "blocker",
    module,
    personId,
    refs: [f.id],
    message: `${box} on ${formName(f)} ${what}.`,
    nextAction: `Enter ${box} exactly as printed on the form.`,
  });
}

function acceptedVariance(decisions: Decision[], subjectId: Id, variance: Dec): boolean {
  return decisions.some((d) => d.kind === "accept_variance" && d.subjectId === subjectId && new D(d.variance).equals(variance));
}

export function analyzeFutures(
  yf: YearFile,
  view: SourceView,
  decisions: Decision[],
  issues: IssueList,
): { result: ModuleResult<FuturesResult>; reconciliation: FuturesReconciliation[] } {
  const accounts = live(yf.accounts)
    .filter((a) => a.kind === "futures_broker")
    .sort((a, b) => (a.id < b.id ? -1 : 1));
  const listed = yf.household.allFuturesAccountsListed;
  if (accounts.length === 0 && listed === "yes") return { result: { state: "not_applicable", issues: [] }, reconciliation: [] };
  if (listed !== "yes")
    issues.add({
      code: listed === "no" ? "ACCOUNTS_MISSING" : "ACCOUNTS_UNCONFIRMED",
      severity: "blocker",
      module: "section1256",
      personId: null,
      refs: [],
      message: listed === "no" ? "Some futures accounts haven't been added yet." : "We need to know this is every futures account you had this year, including closed ones.",
      nextAction: listed === "no" ? "Add the remaining accounts." : "Confirm you've listed every futures account, open or closed.",
    });

  const owners = new Set<Id>();
  for (const a of accounts) {
    if (a.ownerId === "joint") yf.people.forEach((p) => owners.add(p.id));
    else owners.add(a.ownerId);
  }
  for (const p of [...owners].sort()) screeningIssues(yf, p, SCREEN_1256, "section1256", issues);

  const perAccount: FuturesAccountResult[] = [];
  const reconciliation: FuturesReconciliation[] = [];
  const trace: TraceStep[] = [];

  for (const a of accounts) {
    const r = accountBasis(yf, a, view, decisions, issues);
    reconciliation.push(r.recon);
    if (r.basis) {
      perAccount.push({ accountId: a.id, label: a.label, ownerId: a.ownerId, basis: m(r.basis.value), basisFrom: r.basis.from, sourceIds: r.basis.sourceIds });
      trace.push({ label: `${a.label}${r.basis.from === "statement" ? " (from statement)" : r.basis.from === "no_activity" ? " (no trading)" : ""}`, value: fmt(r.basis.value) });
    }
  }

  const total = perAccount.reduce((s, x) => s.plus(x.basis), ZERO);
  const split = split1256(total);
  const perOwner: Record<string, Money> = {};
  for (const x of perAccount) perOwner[x.ownerId] = m(new D(perOwner[x.ownerId] ?? "0").plus(x.basis));
  const value: FuturesResult = { perAccount, perOwner, total: m(total), shortTerm: m(split.value.shortTerm), longTerm: m(split.value.longTerm) };
  return { result: resultFrom(value, [...trace, ...split.trace], issues.for("section1256")), reconciliation };
}

function accountBasis(yf: YearFile, a: Account, view: SourceView, decisions: Decision[], issues: IssueList) {
  const recon: FuturesReconciliation = { accountId: a.id, forms: [], statementDifference: null };
  const personId = a.ownerId === "joint" ? null : a.ownerId;
  if (a.ownerId === "joint" && yf.household.filingStatus !== "mfj")
    issues.add({
      code: "JOINT_ACCOUNT_SPLIT",
      severity: "blocker",
      module: "section1256",
      personId: null,
      refs: [a.id],
      message: `${a.label} is a joint account, but the return isn't joint, so each owner's share has to be worked out.`,
      nextAction: "Ask your preparer how to split this account, or change the filing status if that was a mistake.",
    });
  if (a.ownerId !== "joint" && !yf.people.some((p) => p.id === a.ownerId))
    issues.add({
      code: "OWNER_UNKNOWN",
      severity: "blocker",
      module: "section1256",
      personId: null,
      refs: [a.id],
      message: `${a.label} isn't assigned to anyone on this return.`,
      nextAction: "Choose who owns the account.",
    });

  const forms = view.effectiveForms.filter((f): f is Extract<InfoReturn, { kind: "form_1099b" }> => f.kind === "form_1099b" && f.accountId === a.id);
  const statements = view.all.filter((s): s is Extract<Source, { kind: "broker_statement" }> => s.kind === "broker_statement" && s.accountId === a.id);
  const basisDecision = decisions.find((d): d is Extract<Decision, { kind: "section1256_basis" }> => d.kind === "section1256_basis" && d.accountId === a.id);

  if (forms.length) {
    let sum: Dec | null = ZERO;
    for (const f of forms) {
      const b8 = boxValue(f.box8), b9 = boxValue(f.box9), b10 = boxValue(f.box10);
      const b11 = known(f.box11);
      let computed: Dec | null = null;
      let difference: Dec | null = null;
      // A form that prints only box 11 has nothing to check against.
      const anyDetail = [f.box8, f.box9, f.box10].some((x) => x.state === "value");
      if (b11 && b8 && b9 && b10 && anyDetail) {
        const check = checkBox11({ box8: b8, box9: b9, box10: b10, box11: b11 });
        if (check.state !== "unavailable") computed = check.computed;
        if (check.state === "mismatch") {
          difference = check.difference;
          if (!acceptedVariance(decisions, f.id, check.difference))
            issues.add({
              code: "BOX11_MISMATCH",
              severity: "review",
              module: "section1256",
              personId,
              refs: [f.id],
              message: `On ${formName(f)}, box 8 − box 9 + box 10 is ${usd(check.computed)}, but box 11 says ${usd(b11)} (a ${usd(check.difference)} difference). Box 11 is used as printed.${
                b8.plus(b9).plus(b10).equals(b11) ? " Box 8 + box 9 + box 10 does match, so check whether box 9 was entered with the right sign." : ""
              }`,
              nextAction: "Check the boxes against the form. If they match the form, ask the broker about the difference or note it and continue.",
            });
        }
      }
      recon.forms.push({ sourceId: f.id, box8: f.box8, box9: f.box9, box10: f.box10, box11: f.box11, computedBox11: computed && m(computed), difference: difference && m(difference) });
      if (!b11) {
        boxIssue(f, "Box 11", f.box11, issues, "section1256", personId);
        sum = null;
      } else if (sum) sum = sum.plus(b11);
    }
    if (basisDecision && basisDecision.basis !== "no_activity")
      issues.add({
        code: "STATEMENT_BASIS_IGNORED",
        severity: "info",
        module: "section1256",
        personId,
        refs: [a.id, basisDecision.id],
        message: `${a.label} now has a 1099-B, so it's used instead of the statement figure.`,
        nextAction: "Nothing to do.",
      });
    if (basisDecision?.basis === "no_activity")
      issues.add({
        code: "NO_ACTIVITY_CONFLICT",
        severity: "blocker",
        module: "section1256",
        personId,
        refs: [a.id, basisDecision.id],
        message: `You said ${a.label} had no futures trading, but it has a 1099-B.`,
        nextAction: "Check the form, then either remove it or undo the no-trading answer.",
      });
    const stmtTotal = statements.length === 1 ? statementFigure(statements[0]!) : null;
    if (sum && stmtTotal) recon.statementDifference = m(stmtTotal.minus(sum));
    return { recon, basis: sum ? { value: sum, from: "form_1099b" as const, sourceIds: forms.map((f) => f.id) } : null };
  }

  if (basisDecision?.basis === "no_activity") return { recon, basis: { value: ZERO, from: "no_activity" as const, sourceIds: [] } };

  if (basisDecision?.basis === "statement") {
    const s = statements.find((x) => x.id === basisDecision.sourceId);
    const fig = s ? statementFigure(s) : null;
    const fullYear = s && s.periodFrom !== null && s.periodTo !== null && s.periodFrom <= `${yf.taxYear}-01-01` && s.periodTo >= `${yf.taxYear}-12-31`;
    if (s && fig && fullYear && s.commissionsIncluded === true) {
      issues.add({
        code: "STATEMENT_BASIS",
        severity: "review",
        module: "section1256",
        personId,
        refs: [a.id, s.id],
        message: `${a.label} has no 1099-B, so its figure comes from the year-end statement: realized ${usd(known(s.realizedPnl)!)} − open at last year-end ${usd(known(s.unrealizedPriorYearEnd)!)} + open at this year-end ${usd(known(s.unrealizedYearEnd)!)}.`,
        nextAction: "Confirm with the broker that no 1099-B will be issued, and keep the statement with your records.",
      });
      return { recon, basis: { value: fig, from: "statement" as const, sourceIds: [s.id] } };
    }
    issues.add({
      code: "STATEMENT_INCOMPLETE",
      severity: "blocker",
      module: "section1256",
      personId,
      refs: [a.id, ...(s ? [s.id] : [])],
      message: `To use a statement for ${a.label}, we need a full-year statement with realized profit/loss, open-position value at both year-ends, and commissions included.`,
      nextAction: "Fill in the missing statement figures, or wait for the 1099-B.",
    });
    return { recon, basis: null };
  }

  const status = a.forms.form_1099b ?? null;
  issues.add(
    status === "awaiting"
      ? {
          code: "AWAITING_1099B",
          severity: "blocker",
          module: "section1256",
          personId,
          refs: [a.id],
          message: `Waiting for the 1099-B for ${a.label}. Brokers usually send them in February.`,
          nextAction: "Add it when it arrives.",
        }
      : status === "not_issued"
        ? {
            code: "NEEDS_BASIS",
            severity: "blocker",
            module: "section1256",
            personId,
            refs: [a.id],
            message: `${a.label} has no 1099-B.`,
            nextAction: "Use the year-end statement instead, or confirm the account had no futures trading this year.",
          }
        : {
            code: "FORM_MISSING",
            severity: "blocker",
            module: "section1256",
            personId,
            refs: [a.id],
            message: `${a.label} doesn't have a 1099-B yet.`,
            nextAction: "Add the 1099-B, or tell us it hasn't arrived or won't be issued.",
          },
  );
  return { recon, basis: null };
}

/** Realized − open at prior year-end + open at this year-end, only when every part is known. */
function statementFigure(s: Extract<Source, { kind: "broker_statement" }>): Dec | null {
  const r = known(s.realizedPnl), p = known(s.unrealizedPriorYearEnd), y = known(s.unrealizedYearEnd);
  return r && p && y ? r.minus(p).plus(y) : null;
}
